import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye } from 'lucide-react';
import {
  getPersonalTrabajando,
  getConductores,
  getProduccion,
  getServiciosPublicos,
  getPlanificacionTurnos,
  getTendencia,
  getCalidad,
  getCumplimientoHoras,
  getCostoNominaMes,
  getResumenSecciones,
  getCentrosSeccion,
  setCentrosSeccion,
  getFotosAleatorias,
  getAgentes,
  getPlanificacionCentro,
} from './services/dashboard.services';
import './dashboard.css';
import { AgentesBurbuja } from './components/Agentes';
import { nf, ESTADO_HORAS, estadoHoras, fechaCorta, Modal } from './components/SalaControl';

const useSeccion = (fetcher) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetcher());
    } catch (e) {
      console.error('Error cargando sección del dashboard:', e);
      setError(e?.message || 'No se pudo cargar la información');
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => { cargar(); }, [cargar]);

  return { data, loading, error, recargar: cargar };
};


let sparkSeq = 0;

const Sparkline = ({ vals, color }) => {
  const id = useMemo(() => `spark-${(sparkSeq += 1)}`, []);
  if (!vals || vals.length < 2) return <div className="spark" />;

  const W = 200;
  const H = 34;
  const pad = 3;
  const mn = Math.min(...vals);
  const mxReal = Math.max(...vals);
  const mx = mxReal - mn < 1e-9 ? mn + 1 : mxReal;
  const x = (i) => pad + (i * (W - pad * 2)) / (vals.length - 1);
  const y = (v) => H - pad - ((v - mn) / (mx - mn)) * (H - pad * 2);
  const linea = vals
    .map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`)
    .join(' ');
  const area = `${linea} L${x(vals.length - 1).toFixed(1)} ${H} L${x(0).toFixed(1)} ${H} Z`;

  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.22" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path
        d={linea}
        fill="none"
        stroke={color}
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle
        cx={x(vals.length - 1)}
        cy={y(vals[vals.length - 1])}
        r="3.2"
        fill={color}
        stroke="var(--surface)"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const Delta = ({ valor, invertido }) => {
  if (valor == null) return null;
  const plano = Math.abs(valor) < 0.6;
  const clase = plano ? 'flat' : (valor > 0) !== !!invertido ? 'up' : 'down';
  const flecha = plano ? '±' : valor > 0 ? '▲' : '▼';
  return (
    <span className={`delta ${clase}`} title="Frente al día anterior">
      {flecha} {nf(Math.abs(valor), 1)}%
    </span>
  );
};

const Kpi = ({ label, valor, unidad, sub, serie, color, invertido, variacion }) => (
  <div className="kpi">
    <div className="kpi-top">
      <div>
        <span className="eyebrow">{label}</span>
        <div className="kpi-val num">
          {valor}
          {unidad && <span className="kpi-unit">{unidad}</span>}
        </div>
      </div>
      <Delta valor={variacion} invertido={invertido} />
    </div>
    <Sparkline vals={serie} color={color} />
    <div className="kpi-foot">
      <span className="kpi-sub">{sub}</span>
    </div>
  </div>
);

const SEVERIDAD = {
  no_presentado: {
    sev: 'var(--critical)',
    ink: 'var(--critical-ink)',
    icono: 'M18 6 6 18M6 6l12 12',
  },
  rechazo: {
    sev: 'var(--serious)',
    ink: 'var(--warn-ink)',
    icono: 'M12 3 2 20h20L12 3zM12 9v5M12 17h.01',
  },
  tarde: {
    sev: 'var(--warning)',
    ink: 'var(--warn-ink)',
    icono: 'M12 7v5l3 2M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z',
  },
  // Marcaciones del mismo día que parecen repetidas (doble clic, turnos que se cruzan).
  repetida: {
    sev: 'var(--serious)',
    ink: 'var(--warn-ink)',
    icono: 'M8 8h11v11H8zM5 16V5h11',
  },
  // Más horas ejecutadas que planeadas (sobrecosto de personal).
  sobrecosto_critico: {
    sev: 'var(--critical)',
    ink: 'var(--critical-ink)',
    icono: 'M12 3v18M17 7.5c0-1.9-2.2-3-5-3s-5 1.1-5 3 2.2 2.7 5 3.2 5 1.4 5 3.3-2.2 3-5 3-5-1.1-5-3',
  },
  sobrecosto: {
    sev: 'var(--serious)',
    ink: 'var(--warn-ink)',
    icono: 'M12 3v18M17 7.5c0-1.9-2.2-3-5-3s-5 1.1-5 3 2.2 2.7 5 3.2 5 1.4 5 3.3-2.2 3-5 3-5-1.1-5-3',
  },
  // Dentro de su límite: se lista como referencia, no como alerta.
  neutro: {
    sev: 'var(--rule-strong)',
    ink: 'var(--ink-2)',
    icono: 'M3 12h18',
  },
};

const fechaCortaAlerta = (iso) => {
  if (!iso) return '';
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(a, m - 1, d).toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' });
};

const FilaAlerta = ({ alerta }) => {
  const s = SEVERIDAD[alerta.tipo];
  return (
    <div className="arow" style={{ '--sev': s.sev, '--sev-ink': s.ink }}>
      <div className="badge">
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
          <path d={s.icono} />
        </svg>
      </div>
      <div className="who">
        <div className="name">{alerta.nombre}</div>
        <div className="meta">{alerta.meta}</div>
        {alerta.nota && <div className="nota">{alerta.nota}</div>}
      </div>
      <div className="right">
        <div className="sev-label">{alerta.etiqueta}</div>
        <div className="sev-sub">{alerta.detalle}</div>
      </div>
    </div>
  );
};

const Medidor = ({ hechos, total, porcentaje, unidad = 'centros' }) => {
  const color =
    porcentaje >= 80 ? 'var(--good)' : porcentaje >= 50 ? 'var(--warning)' : 'var(--critical)';
  return (
    <>
      <div className="kpi-top">
        <div>
          <div className="kpi-val num" style={{ fontSize: 30 }}>
            {hechos}
            <span className="kpi-unit">
              de {total} {unidad}
            </span>
          </div>
        </div>
        <span className="delta" style={{ color }}>
          {nf(porcentaje, 1)}%
        </span>
      </div>
      <div className="meter">
        <i style={{ background: color, flex: Math.max(porcentaje, 0.001) }} />
        <i style={{ background: 'var(--surface-3)', flex: Math.max(100 - porcentaje, 0.001) }} />
      </div>
    </>
  );
};



const mesActual = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' }).slice(0, 7);

// Botón de cada sección para sacar o meter centros; avisa cuántos están fuera.
const BotonCentros = ({ seccion, resumen, onAbrir, compacto }) => {
  const fuera = resumen?.[seccion]?.excluidos || 0;
  return (
    <button
      type="button"
      className="ctl"
      onClick={onAbrir}
      title="Elegir qué centros entran a esta sección"
      style={{
        ...(compacto ? { padding: '2px 8px', fontSize: 11 } : {}),
        ...(fuera ? { borderColor: 'var(--warning)', color: 'var(--warn-ink)' } : {}),
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
        <path d="M3 5h18l-7 8v6l-4 2v-8z" />
      </svg>
      Centros{fuera ? ` · ${fuera} fuera` : ''}
    </button>
  );
};

// Configuración compartida (en BD) de qué centros entran a una sección del panel.
const FiltroCentrosModal = ({ seccion, onGuardado, onCerrar }) => {
  const [titulo, setTitulo] = useState('');
  const [centros, setCentros] = useState(null);
  const [marcados, setMarcados] = useState(new Set());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getCentrosSeccion(seccion)
      .then((r) => {
        setTitulo(r.titulo);
        setCentros(r.centros);
        setMarcados(new Set(r.centros.filter((c) => c.incluido).map((c) => c.id)));
      })
      .catch((e) => setError(e?.message || 'No se pudieron cargar los centros'));
  }, [seccion]);

  const alternar = (id) =>
    setMarcados((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await setCentrosSeccion(seccion, [...marcados]);
      onGuardado(seccion);
    } catch (e) {
      setError(e?.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      titulo={`Centros · ${titulo || 'sección'}`}
      subtitulo={
        centros
          ? `${marcados.size} de ${centros.length} centros incluidos · los desmarcados no aparecen en esta sección ni en sus totales. La selección se guarda para todos los usuarios.`
          : 'Cargando…'
      }
      onCerrar={onCerrar}
      ancho={760}
    >
      {error && <div className="empty">{error}</div>}
      {!centros && !error && <div className="empty">Cargando…</div>}
      {centros && (
        <>
          <div className="alert-tabs" style={{ padding: 0, marginBottom: 8 }}>
            <button type="button" className="tab" onClick={() => setMarcados(new Set(centros.map((c) => c.id)))}>
              Todos
            </button>
            <button type="button" className="tab" onClick={() => setMarcados(new Set())}>
              Ninguno
            </button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '4px 16px',
              maxHeight: '55vh',
              overflowY: 'auto',
            }}
          >
            {centros.map((c) => (
              <label
                key={c.id}
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 0', cursor: 'pointer' }}
              >
                <input type="checkbox" checked={marcados.has(c.id)} onChange={() => alternar(c.id)} />
                <span>
                  {c.nombre}
                  {c.ciudad && <span style={{ color: 'var(--ink-3)' }}> · {c.ciudad}</span>}
                </span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="ctl" onClick={onCerrar} disabled={guardando}>
              Cancelar
            </button>
            <button type="button" className="ctl" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar selección'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

const nombreMes = (yyyyMM) => {
  const [y, m] = yyyyMM.split('-').map(Number);
  const texto = new Date(y, m - 1, 1).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};


const DIA_SEMANA_CORTO = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

const DetalleCentroModal = ({ centro, mesEnCurso, horaCorte, mes, onCerrar }) => {
  const [vista, setVista] = useState('dias');
  const e = ESTADO_HORAS[centro.estado];
  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  const maxHoras = Math.max(1, ...centro.dias.map((d) => Math.max(d.planeadas, d.ejecutadas)));

  return (
    <Modal
      titulo={centro.nombre}
      subtitulo={`${nombreMes(mes)} · ${
        centro.cumplimiento == null ? 'sin planificación' : `${nf(centro.cumplimiento, 1)} % del plan a la fecha`
      } · ${nf(centro.ejecutadasACorte, 1)} h ejecutadas de ${nf(centro.planeadasACorte, 1)} h planeadas${
        centro.ejecutadasSinPlan ? ` · ${nf(centro.ejecutadasSinPlan, 1)} h sin plan` : ''
      }`}
      onCerrar={onCerrar}
      acciones={
        <span className="pill" style={{ '--sev': e.sev, '--sev-ink': e.ink }}>
          <i className="dot" />
          {e.texto}
        </span>
      }
    >
      <div className="alert-tabs" role="group" aria-label="Vista del detalle" style={{ padding: '0 0 10px' }}>
        {[
          ['dias', 'Por día'],
          ['semanas', 'Por semana'],
        ].map(([clave, texto]) => (
          <button key={clave} type="button" className="tab" aria-pressed={vista === clave} onClick={() => setVista(clave)}>
            {texto}
          </button>
        ))}
        <span className="legend-inline" style={{ marginLeft: 'auto' }}>
          <span>
            <i className="dot" style={{ background: 'var(--rule-strong)' }} />
            planeadas
          </span>
          <span>
            <i className="dot" style={{ background: 'var(--s1)' }} />
            ejecutadas
          </span>
          <span>
            <i className="dot" style={{ background: 'var(--critical)' }} />
            sobre el plan
          </span>
        </span>
      </div>

      <div className="tbl-scroll">
        {vista === 'dias' ? (
          <table className="data">
            <thead>
              <tr>
                <th>Día</th>
                <th>Planeadas vs. ejecutadas</th>
                <th>Planeadas</th>
                <th>Ejecutadas</th>
                <th>Autorizadas</th>
                <th>Diferencia</th>
                <th>Cumplimiento</th>
                <th>Personas</th>
                <th>Extras</th>
                <th>Nocturnas</th>
              </tr>
            </thead>
            <tbody>
              {centro.dias.map((d) => {
                const esHoy = mesEnCurso && d.fecha === hoy;
                // Hoy se compara solo contra lo que ya debió trabajarse.
                const plan = esHoy ? d.planeadasACorte : d.planeadas;
                const pct = d.planificada && plan ? (d.ejecutadas / plan) * 100 : null;
                const dif = d.ejecutadas - plan;
                const [y, m, dd] = d.fecha.split('-').map(Number);
                const diaSemana = DIA_SEMANA_CORTO[new Date(y, m - 1, dd).getDay()];
                const sinMovimiento = !d.planeadas && !d.ejecutadas;
                return (
                  <tr
                    key={d.fecha}
                    style={{
                      opacity: d.futuro ? 0.45 : sinMovimiento ? 0.6 : 1,
                      background: esHoy ? 'var(--surface-2)' : undefined,
                    }}
                  >
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 500, fontSize: 13 }}>
                        {diaSemana} {dd}
                      </span>
                      {esHoy && <span className="eyebrow"> · hoy hasta {horaCorte}</span>}
                      {!d.planificada && (d.ejecutadas > 0 || !d.futuro) && (
                        <span className="eyebrow"> · sin planificar</span>
                      )}
                    </td>
                    <td>
                      {/* Barra gris = planeado del día; azul = ejecutado; rojo = exceso. */}
                      <div className="bar-track" style={{ width: 140, position: 'relative', marginLeft: 'auto' }}>
                        <div
                          className="bar-fill"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: `${(d.planeadas / maxHoras) * 100}%`,
                            background: 'var(--rule-strong)',
                          }}
                        />
                        {!d.futuro && (
                          <div
                            className="bar-fill"
                            style={{
                              position: 'absolute',
                              top: 2,
                              bottom: 2,
                              left: 0,
                              width: `${(Math.min(d.ejecutadas, plan || d.ejecutadas) / maxHoras) * 100}%`,
                              background: d.planificada ? 'var(--s1)' : 'var(--warning)',
                            }}
                          />
                        )}
                        {!d.futuro && d.planificada && dif > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 2,
                              bottom: 2,
                              left: `${(plan / maxHoras) * 100}%`,
                              width: `${(dif / maxHoras) * 100}%`,
                              background: 'var(--critical)',
                              borderRadius: '0 4px 4px 0',
                            }}
                          />
                        )}
                      </div>
                    </td>
                    <td>{d.planeadas ? nf(d.planeadas, 1) : '—'}</td>
                    <td>{d.futuro ? '' : d.ejecutadas ? nf(d.ejecutadas, 1) : '—'}</td>
                    <td
                      title={
                        d.ajusteAutorizacion
                          ? `La autorización descontó ${nf(d.ajusteAutorizacion, 1)} h de lo marcado`
                          : undefined
                      }
                    >
                      {d.futuro || !d.ejecutadas ? (
                        ''
                      ) : (
                        <>
                          <div style={{ color: d.pendientes > 0 ? 'var(--warn-ink)' : 'var(--good-ink)' }}>
                            {nf(d.autorizadas, 1)}
                            {d.pendientes > 0 ? '' : ' ✓'}
                          </div>
                          {d.pendientes > 0 && (
                            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                              {nf(d.pendientes, 1)} h pend.
                            </div>
                          )}
                          {d.ajusteAutorizacion > 0 && (
                            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                              −{nf(d.ajusteAutorizacion, 1)} h ajuste
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td
                      style={
                        !d.futuro && d.planificada && dif > 0
                          ? { color: 'var(--critical-ink)', fontWeight: 600 }
                          : undefined
                      }
                    >
                      {d.futuro || (!plan && !d.ejecutadas) ? '' : `${dif > 0 ? '+' : ''}${nf(dif, 1)}`}
                    </td>
                    <td style={{ color: ESTADO_HORAS[estadoHoras(pct)].ink }}>
                      {d.futuro || pct == null ? '—' : `${nf(pct, 0)} %`}
                    </td>
                    <td>
                      {d.futuro ? (d.personasPlaneadas ? `${d.personasPlaneadas} plan.` : '') : `${d.personas} / ${d.personasPlaneadas}`}
                    </td>
                    <td style={d.extras > 0 ? { color: 'var(--warn-ink)' } : undefined}>
                      {d.extras ? nf(d.extras, 1) : '—'}
                    </td>
                    <td>{d.nocturnas ? nf(d.nocturnas, 1) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Semana</th>
                <th>Planeadas</th>
                <th>Ejecutadas</th>
                <th>Diferencia</th>
                <th>Cumplimiento</th>
                <th>Extras</th>
                <th>Nocturnas</th>
              </tr>
            </thead>
            <tbody>
              {centro.semanas.map((s) => {
                const pct = s.planificada && s.planeadas ? (s.ejecutadas / s.planeadas) * 100 : null;
                const dif = s.ejecutadas - s.planeadas;
                return (
                  <tr key={s.weekStart}>
                    <td>
                      <span style={{ fontWeight: 500, fontSize: 13 }}>Semana del {fechaCorta(s.weekStart)}</span>
                      {!s.planificada && <span className="eyebrow"> · sin planificar</span>}
                    </td>
                    <td>{s.planificada ? nf(s.planeadas, 1) : '—'}</td>
                    <td>{nf(s.ejecutadas, 1)}</td>
                    <td style={s.planificada && dif > 0 ? { color: 'var(--critical-ink)', fontWeight: 600 } : undefined}>
                      {s.planificada ? `${dif > 0 ? '+' : ''}${nf(dif, 1)}` : '—'}
                    </td>
                    <td style={{ color: ESTADO_HORAS[estadoHoras(pct)].ink }}>
                      {pct == null ? '—' : `${nf(pct, 1)} %`}
                    </td>
                    <td style={s.extras > 0 ? { color: 'var(--warn-ink)' } : undefined}>
                      {s.extras ? nf(s.extras, 1) : '—'}
                    </td>
                    <td>{s.nocturnas ? nf(s.nocturnas, 1) : '—'}</td>
                  </tr>
                );
              })}
              {!centro.semanas.length && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--ink-3)' }}>
                    Sin movimiento en el mes.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <p className="card-sub" style={{ marginTop: 10 }}>
        Personas = empleados que marcaron / empleados planeados ese día. En rojo, las horas trabajadas por
        encima de lo planeado (sobrecosto). Los días en amarillo son de semanas sin planificación.
        Autorizadas = horas de registros ya autorizados (con entrada/salida ajustadas); «pend.» son
        horas aún marcadas sin autorizar y «ajuste» lo que la autorización descontó de lo marcado.
      </p>
    </Modal>
  );
};

const sumarDiasIso = (yyyyMMdd, dias) => {
  const d = new Date(`${yyyyMMdd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
};

const ETIQUETA_DIA = {
  lunes: 'Lun',
  martes: 'Mar',
  miercoles: 'Mié',
  jueves: 'Jue',
  viernes: 'Vie',
  sabado: 'Sáb',
  domingo: 'Dom',
};

const aMinutos = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const minutosAhoraBogota = () => {
  const [h, m] = new Date()
    .toLocaleTimeString('en-GB', { timeZone: 'America/Bogota', hour12: false })
    .split(':');
  return (Number(h) % 24) * 60 + Number(m);
};
const etiquetaHora = (h) => `${String(h).padStart(2, '0')}:00`;

// Intensidad de una celda del mapa: más personas = color más fuerte.
const celdaCalor = (valor, max, color) => {
  if (!valor) return { background: 'var(--surface-2)', color: 'var(--ink-3)' };
  const pct = Math.round(14 + (76 * valor) / Math.max(1, max));
  return {
    background: `color-mix(in srgb, ${color} ${pct}%, var(--surface))`,
    color: pct > 55 ? '#fff' : 'var(--ink)',
  };
};

// Mapa de horas de un día: cuántas personas llegan y cuántas están en turno en
// cada hora (planeado vs. marcado), y la línea de tiempo de cada empleado.
const MapaHorasDia = ({ data, fecha, onCerrar }) => {
  const caja = useRef(null);
  // Al abrirlo desde el encabezado de la tabla, traerlo a la vista.
  useEffect(() => {
    caja.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [fecha]);

  const info = useMemo(() => {
    const esHoy = fecha === data.hoy;
    const futuro = fecha > data.hoy;
    const ahora = esHoy ? minutosAhoraBogota() : null;

    const filas = data.empleados
      .map((e) => {
        const d = e.dias.find((x) => x.fecha === fecha) || { rangos: [], registros: [] };
        const turnos = d.rangos
          .map((r) => {
            const ini = aMinutos(r.inicio);
            let fin = aMinutos(r.fin);
            if (ini == null || fin == null) return null;
            if (fin <= ini) fin = 24 * 60; // turno nocturno: se muestra hasta medianoche
            return { ini, fin, etiqueta: `${r.inicio}–${r.fin}` };
          })
          .filter(Boolean);
        const finPlaneado = turnos.length ? Math.max(...turnos.map((t) => t.fin)) : null;
        const marcas = d.registros
          .map((r) => {
            const ini = aMinutos(r.horaIngreso);
            if (ini == null) return null;
            let fin = aMinutos(r.horaSalida);
            const abierta = fin == null;
            // Sin salida: hoy llega hasta la hora actual; en días pasados, hasta el fin del turno.
            if (abierta) fin = esHoy ? Math.max(ini, ahora) : Math.max(ini, finPlaneado ?? ini);
            else if (fin < ini) fin = 24 * 60;
            return {
              ini,
              fin,
              abierta,
              etiqueta: `marcó ${r.horaIngreso}–${r.horaSalida || (esHoy ? 'en turno' : 'sin salida')}`,
            };
          })
          .filter(Boolean);
        return { id: e.id, nombre: e.nombre, rol: e.rol, colorRol: e.colorRol, turnos, marcas };
      })
      .filter((f) => f.turnos.length || f.marcas.length);

    const primerInicio = (f) => Math.min(...[...f.turnos, ...f.marcas].map((x) => x.ini));
    filas.sort((a, b) => primerInicio(a) - primerInicio(b) || a.nombre.localeCompare(b.nombre));

    // Rango de horas visible: del primer ingreso a la última salida, con una hora de margen.
    const minutos = filas.flatMap((f) => [...f.turnos, ...f.marcas].flatMap((x) => [x.ini, x.fin]));
    let h0 = minutos.length ? Math.floor(Math.min(...minutos) / 60) - 1 : 6;
    let h1 = minutos.length ? Math.ceil(Math.max(...minutos) / 60) + 1 : 20;
    h0 = Math.max(0, h0);
    h1 = Math.min(24, Math.max(h1, h0 + 6));
    const horas = Array.from({ length: h1 - h0 }, (_, i) => h0 + i);

    const cubre = (lista, h) => lista.some((x) => x.ini < (h + 1) * 60 && x.fin > h * 60);
    const porHora = horas.map((h) => ({
      h,
      llegadasPlan: filas.reduce((s, f) => s + f.turnos.filter((t) => Math.floor(t.ini / 60) === h).length, 0),
      llegadasReal: filas.reduce((s, f) => s + f.marcas.filter((m) => Math.floor(m.ini / 60) === h).length, 0),
      enTurno: filas.filter((f) => cubre(f.turnos, h)).length,
      presentes: filas.filter((f) => cubre(f.marcas, h)).length,
    }));

    const maxDe = (k) => Math.max(0, ...porHora.map((p) => p[k]));
    const max = {
      llegadas: Math.max(maxDe('llegadasPlan'), maxDe('llegadasReal')),
      turno: Math.max(maxDe('enTurno'), maxDe('presentes')),
    };
    const pico = porHora.reduce((a, p) => (p.llegadasPlan > (a?.llegadasPlan ?? 0) ? p : a), null);
    const horasMaxTurno = porHora.filter((p) => p.enTurno === maxDe('enTurno') && p.enTurno > 0);

    return { filas, horas, porHora, h0, h1, esHoy, futuro, ahora, max, pico, horasMaxTurno };
  }, [data, fecha]);

  const { filas, horas, porHora, h0, h1, esHoy, futuro, ahora, max, pico, horasMaxTurno } = info;
  const diaInfo = data.dias.find((d) => d.fecha === fecha);
  const pos = (min) => `${((min - h0 * 60) / ((h1 - h0) * 60)) * 100}%`;
  const ancho = (a, b) => `${((b - a) / ((h1 - h0) * 60)) * 100}%`;
  const columnas = { gridTemplateColumns: `190px repeat(${horas.length}, minmax(32px, 1fr))` };
  const lineaAhora = esHoy && ahora >= h0 * 60 && ahora <= h1 * 60;

  const filaCalor = (etiqueta, clave, maximo, color) => (
    <>
      <div className="mapa-etq">{etiqueta}</div>
      {porHora.map((p) => (
        <div
          key={p.h}
          className="mapa-celda"
          style={celdaCalor(p[clave], maximo, color)}
          title={`${etiquetaHora(p.h)}–${etiquetaHora(p.h + 1)}: ${p[clave]} ${p[clave] === 1 ? 'persona' : 'personas'}`}
        >
          {p[clave] || ''}
        </div>
      ))}
    </>
  );

  return (
    <div className="mapa-dia" ref={caja}>
      <div className="mapa-cab">
        <div style={{ minWidth: 0 }}>
          <strong>
            Mapa de horas · {diaInfo ? `${ETIQUETA_DIA[diaInfo.dia]} ` : ''}
            {fechaCorta(fecha)}
            {esHoy ? ' · hoy' : ''}
          </strong>
          <div className="card-sub" style={{ margin: '2px 0 0' }}>
            {filas.length
              ? [
                  pico?.llegadasPlan
                    ? `Pico de llegada: ${etiquetaHora(pico.h)} (${pico.llegadasPlan} ${pico.llegadasPlan === 1 ? 'persona' : 'personas'})`
                    : null,
                  horasMaxTurno.length
                    ? `Máximo en turno: ${horasMaxTurno[0].enTurno} personas de ${etiquetaHora(horasMaxTurno[0].h)} a ${etiquetaHora(horasMaxTurno[horasMaxTurno.length - 1].h + 1)}`
                    : null,
                  diaInfo ? `${diaInfo.personas} plan. · ${diaInfo.marcaron} marcaron` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Nadie tiene turno ni marcación este día.'}
          </div>
        </div>
        <span className="spacer" />
        <button type="button" className="ctl" onClick={onCerrar}>
          Ocultar
        </button>
      </div>

      {filas.length > 0 && (
        <>
          <div className="mapa-scroll">
            <div className="mapa-grid" style={columnas}>
              <div className="mapa-etq" />
              {horas.map((h) => (
                <div key={h} className="mapa-hora">
                  {String(h).padStart(2, '0')}
                </div>
              ))}

              {filaCalor('Llegadas planeadas', 'llegadasPlan', max.llegadas, 'var(--s1)')}
              {!futuro && filaCalor('Llegadas reales', 'llegadasReal', max.llegadas, 'var(--s3)')}
              {filaCalor('En turno (planeado)', 'enTurno', max.turno, 'var(--s1)')}
              {!futuro && filaCalor('Presentes (marcaron)', 'presentes', max.turno, 'var(--s3)')}

              <div className="mapa-sep" style={{ gridColumn: '1 / -1' }} />

              {filas.map((f) => (
                <div key={f.id} className="mapa-fila" style={{ gridColumn: '1 / -1', ...columnas }}>
                  <div className="mapa-etq" title={f.nombre}>
                    <i className="dot" style={{ background: f.colorRol || 'var(--rule-strong)' }} />
                    <span className="mapa-nombre">{f.nombre}</span>
                  </div>
                  <div
                    className="mapa-pista"
                    style={{ gridColumn: '2 / -1', backgroundSize: `calc(100% / ${horas.length}) 100%` }}
                  >
                    {f.turnos.map((t) => (
                      <div
                        key={`t${t.ini}`}
                        className="mapa-turno"
                        style={{ left: pos(t.ini), width: ancho(t.ini, t.fin) }}
                        title={`Turno ${t.etiqueta}`}
                      >
                        <span>{t.etiqueta}</span>
                      </div>
                    ))}
                    {f.marcas.map((m) => (
                      <div
                        key={`m${m.ini}`}
                        className={`mapa-marca${m.abierta ? ' abierta' : ''}`}
                        style={{ left: pos(m.ini), width: ancho(m.ini, Math.max(m.fin, m.ini + 4)) }}
                        title={m.etiqueta}
                      />
                    ))}
                    {lineaAhora && <div className="mapa-ahora" style={{ left: pos(ahora) }} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mapa-leyenda">
            <span><i className="lg turno" /> Turno planeado</span>
            {!futuro && <span><i className="lg marca" /> Marcación (ingreso → salida)</span>}
            {!futuro && <span><i className="lg abierta" /> Sin salida registrada</span>}
            {lineaAhora && <span><i className="lg ahora" /> Hora actual</span>}
            <span>El número de cada celda es la cantidad de personas en esa hora.</span>
          </div>
        </>
      )}
    </div>
  );
};

// Planeado vs. ejecutado en una celda; el ejecutado en rojo si supera el plan.
// Debajo, cuánto de lo ejecutado ya está autorizado.
const TotalPlanEjec = ({ plan, ejec, aut }) => (
  <>
    <div style={{ fontWeight: 600 }}>{nf(plan, 1)} h plan.</div>
    {ejec != null && (
      <div
        style={{
          fontSize: 12,
          color: ejec > plan ? 'var(--critical-ink)' : 'var(--s1)',
          fontWeight: ejec > plan ? 600 : 400,
        }}
      >
        {nf(ejec, 1)} h ejec.
        {plan > 0 && ` · ${nf((ejec / plan) * 100, 0)} %`}
      </div>
    )}
    {ejec != null && aut != null && ejec > 0 && (
      <div className="plan-sub" style={{ color: aut < ejec ? 'var(--warn-ink)' : 'var(--good-ink)' }}>
        {nf(aut, 1)} h aut.{aut < ejec ? ` · ${nf(ejec - aut, 1)} h por autorizar` : ' ✓'}
      </div>
    )}
  </>
);

// Planificación semanal de un centro: turnos por empleado y día, con lo que
// cada uno marcó para comparar contra lo planeado.
const PlanificacionCentroModal = ({ centro, semanaInicial, onCerrar }) => {
  const [semana, setSemana] = useState(semanaInicial || null);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  // Día con el mapa de horas abierto (botón del ojo en el encabezado del día)
  const [diaMapa, setDiaMapa] = useState(null);

  useEffect(() => {
    setDiaMapa(null);
  }, [semana]);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setError(null);
    getPlanificacionCentro(centro.id, semana || undefined)
      .then((r) => {
        if (!vigente) return;
        setData(r);
        if (!semana) setSemana(r.weekStart);
      })
      .catch((e) => vigente && setError(e?.message || 'No se pudo cargar la planificación'))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [centro.id, semana]);

  const mover = (dias) => setSemana((s) => (s ? sumarDiasIso(s, dias) : s));

  return (
    <Modal
      titulo={`Planificación · ${centro.nombre}`}
      subtitulo={
        data
          ? `Semana del ${fechaCorta(data.weekStart)} al ${fechaCorta(data.weekEnd)} · ${
              data.empleados.length
            } empleados · ${nf(data.totalHoras, 1)} h planeadas · ${nf(
              data.totalEjecutadas + data.totalEjecutadasSinPlan,
              1,
            )} h ejecutadas${
              data.totalEjecutadasSinPlan ? ` (${nf(data.totalEjecutadasSinPlan, 1)} h sin planificar)` : ''
            }`
          : 'Cargando…'
      }
      onCerrar={onCerrar}
      ancho={1200}
      acciones={
        <>
          <button type="button" className="ctl" onClick={() => mover(-7)} disabled={cargando}>
            ← Semana anterior
          </button>
          <button type="button" className="ctl" onClick={() => setSemana(semanaInicial)} disabled={cargando}>
            Esta semana
          </button>
          <button type="button" className="ctl" onClick={() => mover(7)} disabled={cargando}>
            Semana siguiente →
          </button>
        </>
      }
    >
      {error && <div className="empty">{error}</div>}
      {!error && data && (
        <div style={{ opacity: cargando ? 0.5 : 1, transition: 'opacity .2s' }}>
          {!data.empleados.length ? (
            <div className="empty">Este centro no tiene planificación para la semana.</div>
          ) : (
            <>
            {diaMapa && <MapaHorasDia data={data} fecha={diaMapa} onCerrar={() => setDiaMapa(null)} />}
            <div className="tbl-scroll">
              <table className="data plan-semana">
                <thead>
                  <tr>
                    <th>Empleado</th>
                    {data.dias.map((d) => (
                      <th key={d.fecha} style={d.fecha === data.hoy ? { color: 'var(--ink)' } : undefined}>
                        <div className="plan-th">
                          <span>
                            {ETIQUETA_DIA[d.dia]} {Number(d.fecha.slice(8))}
                            {d.fecha === data.hoy ? ' · hoy' : ''}
                          </span>
                          <button
                            type="button"
                            className={`plan-ojo${diaMapa === d.fecha ? ' activo' : ''}`}
                            onClick={() => setDiaMapa((x) => (x === d.fecha ? null : d.fecha))}
                            title="Ver mapa de horas del día"
                            aria-label={`Ver mapa de horas del ${ETIQUETA_DIA[d.dia]} ${Number(d.fecha.slice(8))}`}
                            aria-pressed={diaMapa === d.fecha}
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      </th>
                    ))}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.empleados.map((e) => (
                    <tr key={e.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <i
                            className="dot"
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              flex: 'none',
                              background: e.colorRol || 'var(--rule-strong)',
                            }}
                          />
                          <span style={{ fontWeight: 500, fontSize: 13 }}>{e.nombre}</span>
                        </div>
                        <div className="eyebrow">
                          {[e.rol, e.tipoHorario].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </td>
                      {e.dias.map((d) => {
                        const pasado = d.fecha < data.hoy;
                        const marco = d.registros.length > 0;
                        // Planeado y sin marcar en un día ya pasado = ausencia.
                        const ausente = pasado && d.horas > 0 && !marco;
                        const sinTurno = !d.rangos.length;
                        return (
                          <td
                            key={d.fecha}
                            className={`plan-celda${sinTurno ? ' descanso' : ''}${ausente ? ' ausente' : ''}${
                              sinTurno && marco ? ' extra' : ''
                            }`}
                          >
                            {sinTurno ? (
                              <div className="plan-turno">Descanso</div>
                            ) : (
                              d.rangos.map((r) => (
                                <div key={`${r.inicio}-${r.fin}`} className="plan-turno">
                                  {r.inicio}–{r.fin}
                                </div>
                              ))
                            )}
                            {!sinTurno && (
                              <div className="plan-sub">
                                {nf(d.horas, 1)} h{d.almuerzo ? ` · alm. ${d.almuerzo} min` : ''}
                              </div>
                            )}
                            {d.registros.map((r, i) =>
                              r.autorizado ? (
                                <div key={i} className="plan-marca autorizada">
                                  <div>
                                    ✓ aut. {r.horaIngresoAutorizada}–{r.horaSalidaAutorizada} · {nf(r.horas, 1)} h
                                  </div>
                                  {(r.horaIngresoAutorizada !== r.horaIngreso ||
                                    r.horaSalidaAutorizada !== r.horaSalida ||
                                    r.horasMarcadas !== r.horas) && (
                                    <div className="plan-marcado">
                                      marcó {r.horaIngreso}–{r.horaSalida} · {nf(r.horasMarcadas, 1)} h
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div key={i} className="plan-marca">
                                  marcó {r.horaIngreso || '—'}–{r.horaSalida || 'en turno'} · {nf(r.horas, 1)} h
                                  {r.horaSalida && <span className="plan-pendiente"> · por autorizar</span>}
                                </div>
                              ),
                            )}
                            {ausente && <div className="plan-marca">sin marcar</div>}
                          </td>
                        );
                      })}
                      <td>
                        <TotalPlanEjec plan={e.totalHoras} ejec={e.totalEjecutadas} aut={e.totalAutorizadas} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Total del día</td>
                    {data.dias.map((d) => (
                      <td key={d.fecha}>
                        <TotalPlanEjec
                          plan={d.horas}
                          ejec={d.fecha > data.hoy ? null : d.ejecutadas}
                          aut={d.autorizadas}
                        />
                        <div className="plan-sub">
                          {d.personas} plan. · {d.marcaron} marcaron
                        </div>
                        {d.ejecutadasSinPlan > 0 && (
                          <div className="plan-sub" style={{ color: 'var(--warn-ink)' }}>
                            +{nf(d.ejecutadasSinPlan, 1)} h sin planificar
                          </div>
                        )}
                      </td>
                    ))}
                    <td>
                      <TotalPlanEjec plan={data.totalHoras} ejec={data.totalEjecutadas} aut={data.totalAutorizadas} />
                      {data.totalEjecutadasSinPlan > 0 && (
                        <div className="plan-sub" style={{ color: 'var(--warn-ink)' }}>
                          +{nf(data.totalEjecutadasSinPlan, 1)} h sin planificar
                        </div>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            </>
          )}

          {data.sinPlanificar.length > 0 && (
            <div className="plan-aviso">
              <strong>Marcaron sin estar planificados esta semana:</strong>{' '}
              {data.sinPlanificar
                .map(
                  (e) =>
                    `${e.nombre} (${e.dias.map((f) => fechaCorta(f)).join(', ')} · ${nf(e.horas, 1)} h)`,
                )
                .join(' · ')}
            </div>
          )}
          <p className="card-sub" style={{ marginTop: 10 }}>
            Cada celda muestra el turno planeado y debajo lo que el empleado marcó. En rojo, turnos planeados
            de días pasados sin marcación; en amarillo, marcaciones en días de descanso. Ejecutadas = horas
            autorizadas o, si aún no se autorizan, las marcadas; un turno abierto cuenta hasta este momento.
            En rojo, el ejecutado que supera lo planeado.
            {data.weekStart <= data.hoy &&
              data.hoy <= data.weekEnd &&
              ' En la semana en curso el total ejecutado va hasta hoy, mientras el planeado es de la semana completa.'}
          </p>
        </div>
      )}
    </Modal>
  );
};

const fotoSrc = (foto) =>
  /^(https?:|data:)/.test(foto || '') ? foto : `data:image/jpeg;base64,${foto}`;

// Muestra al azar de fotos del registro de ingreso para revisar el uniforme.
const FotosUniformeModal = ({ onCerrar }) => {
  const [fecha, setFecha] = useState(() =>
    new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' }),
  );
  const [centro, setCentro] = useState('');
  const [centros, setCentros] = useState([]);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [ampliada, setAmpliada] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const respuesta = await getFotosAleatorias({ fecha, centro: centro || undefined, cantidad: 12 });
      setData(respuesta);
      // El backend solo lista centros cuando no se filtra por uno.
      if (!centro) setCentros(respuesta.centros || []);
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar las fotos');
    } finally {
      setCargando(false);
    }
  }, [fecha, centro]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return (
    <Modal
      titulo="Revisión de uniformes"
      subtitulo={
        data
          ? `${data.fotos.length} fotos al azar de ${data.total} registros del ${fechaCorta(data.fecha)}`
          : 'Fotos al azar del registro de ingreso'
      }
      onCerrar={onCerrar}
      ancho={1100}
      acciones={
        <>
          <input
            type="date"
            className="ctl"
            aria-label="Fecha"
            value={fecha}
            max={new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' })}
            onChange={(e) => {
              if (!e.target.value) return;
              setCentro('');
              setFecha(e.target.value);
            }}
          />
          <select className="ctl" aria-label="Centro" value={centro} onChange={(e) => setCentro(e.target.value)}>
            <option value="">Todos los centros</option>
            {centros.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre} ({c.registros})
              </option>
            ))}
          </select>
          <button type="button" className="ctl" onClick={cargar} disabled={cargando}>
            {cargando ? 'Cargando…' : 'Otra tanda'}
          </button>
        </>
      }
    >
      {error && <div className="empty">{error}</div>}
      {!error && data && !data.fotos.length && (
        <div className="empty">No hay fotos de registro para este día.</div>
      )}
      {data && data.fotos.length > 0 && (
        <div className="fotos-grid" style={{ opacity: cargando ? 0.5 : 1 }}>
          {data.fotos.map((f) => (
            <button key={f.id} type="button" className="foto-card" onClick={() => setAmpliada(f)}>
              <img src={fotoSrc(f.foto)} alt={`Registro de ${f.nombre}`} loading="lazy" />
              <div className="foto-pie">
                <div className="name">{f.nombre}</div>
                <div className="meta">
                  {f.centro} · entró {f.horaIngreso || '—'}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {ampliada && (
        <div className="modal-fondo" style={{ zIndex: 1001 }} onClick={() => setAmpliada(null)} role="presentation">
          <figure className="foto-ampliada" onClick={(e) => e.stopPropagation()}>
            <img src={fotoSrc(ampliada.foto)} alt={`Registro de ${ampliada.nombre}`} />
            <figcaption>
              <strong>{ampliada.nombre}</strong>
              {ampliada.rol ? ` · ${ampliada.rol}` : ''} · {ampliada.centro} · entró {ampliada.horaIngreso || '—'}
              {ampliada.horaSalida ? ` · salió ${ampliada.horaSalida}` : ''}
              <button type="button" className="ctl" style={{ marginLeft: 12 }} onClick={() => setAmpliada(null)}>
                Cerrar
              </button>
            </figcaption>
          </figure>
        </div>
      )}
    </Modal>
  );
};

// Conductores: planeado vs. ejecutado por persona (no suman en los centros de arriba).
const ConductoresDelMes = ({ conductores, limiteSemanal }) => {
  if (!conductores?.filas?.length) return null;
  const t = conductores.totales;
  const e = ESTADO_HORAS[estadoHoras(t.cumplimiento)];
  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="card-head" style={{ padding: '12px 16px 0' }}>
        <h3>Conductores</h3>
        <span className="spacer" />
        <span className="eyebrow">
          {t.conductores} conductores · {nf(t.ejecutadasACorte, 1)} h ejecutadas de {nf(t.planeadasACorte, 1)} h planeadas
          {t.cumplimiento != null ? ` · ${nf(t.cumplimiento, 1)} %` : ''}
        </span>
      </div>
      <div className="tbl-scroll">
        <table className="data">
          <thead>
            <tr>
              <th>Conductor</th>
              <th>Cumplimiento</th>
              <th>Planeadas</th>
              <th>Ejecutadas</th>
              <th>Diferencia</th>
              <th>Sin plan</th>
              <th>Extras</th>
              <th>Nocturnas</th>
            </tr>
          </thead>
          <tbody>
            {conductores.filas.map((k) => {
              const ek = ESTADO_HORAS[k.estado] || e;
              return (
                <tr key={k.id}>
                  <td>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{k.nombre}</div>
                    <div className="eyebrow">{k.centros.join(' · ') || '—'}</div>
                  </td>
                  <td>
                    <div className="bar-cell">
                      <span className="pill" style={{ '--sev': ek.sev, '--sev-ink': ek.ink }}>
                        <i className="dot" />
                        {k.cumplimiento == null ? ek.texto : `${nf(k.cumplimiento, 1)} %`}
                      </span>
                      <div className="bar-track" style={{ width: 64 }}>
                        <div className="bar-fill" style={{ width: `${Math.min(100, k.cumplimiento || 0)}%`, background: ek.sev }} />
                      </div>
                    </div>
                  </td>
                  <td>{nf(k.planeadasACorte, 1)}</td>
                  <td>{nf(k.ejecutadasACorte, 1)}</td>
                  <td style={k.diferencia < 0 ? { color: 'var(--critical-ink)' } : undefined}>
                    {k.diferencia > 0 ? '+' : ''}
                    {nf(k.diferencia, 1)}
                  </td>
                  <td style={k.ejecutadasSinPlan > 0 ? { color: 'var(--warn-ink)' } : undefined}>
                    {k.ejecutadasSinPlan ? nf(k.ejecutadasSinPlan, 1) : '—'}
                  </td>
                  <td style={k.horasExtras > 0 ? { color: 'var(--warn-ink)' } : undefined}>
                    {k.horasExtras ? nf(k.horasExtras, 1) : '—'}
                  </td>
                  <td>{k.horasNocturnas ? nf(k.horasNocturnas, 1) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ fontWeight: 600 }}>Total conductores</td>
              <td>{t.cumplimiento == null ? '—' : `${nf(t.cumplimiento, 1)} %`}</td>
              <td>{nf(t.planeadasACorte, 1)}</td>
              <td>{nf(t.ejecutadasACorte, 1)}</td>
              <td>{nf(t.ejecutadasACorte - t.planeadasACorte, 1)}</td>
              <td>{t.ejecutadasSinPlan ? nf(t.ejecutadasSinPlan, 1) : '—'}</td>
              <td>{t.horasExtras ? nf(t.horasExtras, 1) : '—'}</td>
              <td>{t.horasNocturnas ? nf(t.horasNocturnas, 1) : '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="card-sub" style={{ margin: '8px 16px 12px' }}>
        Conductor = empleado con rol «Conductor». Se compara contra su propia planificación: lo trabajado
        en semanas en que el conductor no tenía turno planificado aparece en «Sin plan». Extras = lo que
        excede {limiteSemanal} h en la semana.
      </p>
    </div>
  );
};

// Pesos en millones para las tarjetas y la tabla: $ 56,3 M.
const millones = (v) => (v == null ? '—' : `$ ${nf(v / 1e6, 1)} M`);
const pesosCop = (v) => `$ ${nf(v)}`;

const FUENTE_QUINCENA = {
  nomina: 'nómina generada',
  marcaciones: 'marcaciones',
  'marcaciones+plan': 'marcado a hoy + plan',
  plan: 'solo plan',
};

// Color de la diferencia de costo: por encima del plan es sobrecosto (rojo desde el 10 %).
const colorDiferencia = (variacion) =>
  variacion == null || variacion <= 0 ? undefined : variacion > 10 ? 'var(--critical-ink)' : 'var(--warn-ink)';

const CostoNominaMes = ({ costo }) => {
  const { data, error } = costo;
  if (error) return <div className="panel empty" style={{ marginTop: 12 }}>Costo de la nómina: {error}</div>;
  // Sin datos: espera a que «Horas del mes» cargue sus centros o a que termine el cálculo.
  if (!data) return <div className="panel empty" style={{ marginTop: 12 }}>Calculando el costo de la nómina…</div>;
  const t = data.totales;
  const cerrado = data.hoy > data.hasta;
  const futuro = data.hoy < data.desde;
  const etiquetaProy = cerrado ? 'Costo real del mes' : futuro ? 'Costo proyectado (solo plan)' : 'Costo proyectado al cierre';
  const pe = data.parametrosCostoEmpresa || {};
  const explicacion = [
    'Costo total empresa = devengado + aportes del empleador + prestaciones, con el mismo motor de la nómina por periodo (horas base de cada quincena, recargos, extras, auxilio de transporte, incapacidades registradas).',
    `Aportes: pensión ${nf(pe.pensionPct, 2)} %, ARL ${nf(pe.arlPct, 3)} %, caja ${nf(pe.cajaPct, 2)} %${pe.exoneracion114 ? ' (salud, ICBF y SENA solo desde 10 SMMLV)' : ''}. Prestaciones: prima, cesantías, intereses y vacaciones.`,
    'Planeado: los turnos de la planificación semanal del mes completo, con su almuerzo descontado.',
    cerrado
      ? 'Real: las nóminas generadas de cada quincena o, si no hay, las marcaciones.'
      : 'Proyectado: lo marcado hasta hoy (hoy cuenta lo marcado si la persona ya cerró turno) más lo planeado de los días que faltan.',
    'El costo de cada persona se reparte entre centros según sus horas en cada uno.',
  ].join('\n\n');

  return (
    <>
      <div className="sec-head" style={{ marginTop: 18 }}>
        <h3 style={{ margin: 0 }}>Costo de la nómina del mes</h3>
        <span className="note" title={explicacion} style={{ cursor: 'help' }}>
          ⓘ {data.quincenas.map((q) => `${fechaCorta(q.desde)}–${fechaCorta(q.hasta)}: ${FUENTE_QUINCENA[q.fuente] || q.fuente}`).join(' · ')}
        </span>
      </div>
      <div className="kpis">
        <div className="kpi lead" title={explicacion}>
          <span className="eyebrow">{etiquetaProy}</span>
          <div className="kpi-val num" style={{ color: colorDiferencia(t.variacionPct) }}>
            {millones(t.proyectado.costoEmpresa)}
          </div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              Devengado {millones(t.proyectado.devengado)} · aportes {millones(t.proyectado.aportes)} · prestaciones{' '}
              {millones(t.proyectado.prestaciones)} · {nf(t.proyectado.horas)} h
            </span>
          </div>
        </div>

        <div className="kpi" title={explicacion}>
          <span className="eyebrow">Costo planeado</span>
          <div className="kpi-val num">{millones(t.planeado.costoEmpresa)}</div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              Si se cumple la planificación · devengado {millones(t.planeado.devengado)} · {nf(t.planeado.horas)} h
            </span>
          </div>
        </div>

        <div className="kpi">
          <span className="eyebrow">{t.diferencia > 0 ? 'Sobrecosto vs. plan' : 'Diferencia vs. plan'}</span>
          <div className="kpi-val num" style={{ color: colorDiferencia(t.variacionPct) }}>
            {t.diferencia > 0 ? '+' : ''}
            {millones(t.diferencia)}
          </div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              {t.variacionPct == null ? 'Sin planificación en el mes' : `${t.variacionPct > 0 ? '+' : ''}${nf(t.variacionPct, 1)} % sobre lo planeado`}
              {' · '}
              {nf(data.centros.filter((c) => c.diferencia > 0).length)} centros por encima
            </span>
          </div>
        </div>

        <div className="kpi">
          <span className="eyebrow">Costo por hora trabajada</span>
          <div className="kpi-val num">{t.proyectado.costoHora ? pesosCop(t.proyectado.costoHora) : '—'}</div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              Costo total empresa ÷ horas · planeado {t.planeado.costoHora ? pesosCop(t.planeado.costoHora) : '—'}
              {data.conductores.proyectado.costoEmpresa > 0 &&
                ` · conductores aparte: ${millones(data.conductores.proyectado.costoEmpresa)}`}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

const CeldasCosto = ({ c, cargando }) => {
  if (!c) {
    const vacio = cargando ? '…' : '—';
    return (
      <>
        <td>{vacio}</td>
        <td>{vacio}</td>
        <td>{vacio}</td>
      </>
    );
  }
  return (
    <>
      <td title={`Devengado ${pesosCop(c.planeado.devengado)} · ${nf(c.planeado.horas, 1)} h`}>{millones(c.planeado.costoEmpresa)}</td>
      <td
        title={`Devengado ${pesosCop(c.proyectado.devengado)} · aportes ${pesosCop(c.proyectado.aportes)} · prestaciones ${pesosCop(
          c.proyectado.prestaciones,
        )} · ${nf(c.proyectado.horas, 1)} h · ${c.proyectado.personas} personas`}
      >
        {millones(c.proyectado.costoEmpresa)}
      </td>
      <td style={{ color: colorDiferencia(c.variacionPct) }}>
        {c.diferencia > 0 ? '+' : ''}
        {millones(c.diferencia)}
        {c.variacionPct != null && (
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {c.variacionPct > 0 ? '+' : ''}
            {nf(c.variacionPct, 1)} %
          </div>
        )}
      </td>
    </>
  );
};

const HorasDelMes = ({ seccion, costo }) => {
  const [detalle, setDetalle] = useState(null);
  const { data, error } = seccion;
  const costoPorCentro = new Map((costo?.data?.centros || []).map((c) => [c.id, c]));

  if (error) return <div className="panel empty">{error}</div>;
  if (!data) return <div className="panel empty">Cargando…</div>;

  const t = data.totales;
  const estadoGlobal = estadoHoras(t.cumplimiento);
  const avanceMes = data.diasMes ? (data.diasTranscurridos / data.diasMes) * 100 : 0;
  const sinDiasCerrados = data.diasTranscurridos === 0;
  const trabajadas = t.ejecutadasACorte + t.ejecutadasSinPlan;

  // Centros que trabajaron más horas de las planeadas: sobrecosto de personal.
  const conSobrecosto = data.centros
    .filter((c) => c.horasNoPlaneadas > 0)
    .sort((a, b) => b.horasNoPlaneadas - a.horasNoPlaneadas);

  return (
    <>
      <div className="kpis">
        <div className="kpi lead">
          <div className="kpi-top">
            <div>
              <span className="eyebrow">Cumplimiento a la fecha</span>
              <div className="kpi-val num" style={{ color: ESTADO_HORAS[estadoGlobal].ink }}>
                {t.cumplimiento == null ? '—' : nf(t.cumplimiento, 1)}
                <span className="kpi-unit">%</span>
              </div>
            </div>
          </div>
          <div className="meter">
            <i style={{ background: 'var(--s1)', flex: Math.max(avanceMes, 0.001) }} />
            <i style={{ background: 'var(--surface-3)', flex: Math.max(100 - avanceMes, 0.001) }} />
          </div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              {sinDiasCerrados
                ? 'Aún no hay días cerrados en el mes'
                : `${nf(t.ejecutadasACorte)} h ejecutadas de ${nf(t.planeadasACorte)} h planeadas · día ${data.diasTranscurridos} de ${data.diasMes}${
                    data.horaCorte ? ` · hasta las ${data.horaCorte}` : ''
                  }`}
            </span>
          </div>
        </div>

        <div className="kpi">
          <span className="eyebrow">Horas por encima del plan</span>
          <div
            className="kpi-val num"
            style={t.horasNoPlaneadas > 0 ? { color: 'var(--critical-ink)' } : undefined}
          >
            {nf(t.horasNoPlaneadas, 1)}
            <span className="kpi-unit">h</span>
          </div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              {nf(t.horasSobrePlan, 1)} h sobre lo planeado · {nf(t.ejecutadasSinPlan, 1)} h en semanas
              sin planificar · {t.centrosConSobrecosto} centros sobre el 100 %
            </span>
          </div>
        </div>

        <div className="kpi">
          <span className="eyebrow">Horas autorizadas</span>
          <div className="kpi-val num">
            {nf(
              t.horasAutorizadas + t.horasPendientesAutorizar
                ? (t.horasAutorizadas / (t.horasAutorizadas + t.horasPendientesAutorizar)) * 100
                : 0,
              1,
            )}
            <span className="kpi-unit">%</span>
          </div>
          <div className="meter">
            <i style={{ background: 'var(--good)', flex: Math.max(t.horasAutorizadas, 0.001) }} />
            <i style={{ background: 'var(--warning)', flex: Math.max(t.horasPendientesAutorizar, 0.001) }} />
          </div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              {nf(t.horasAutorizadas, 1)} h autorizadas ({t.registrosAutorizados} registros) ·{' '}
              {nf(t.horasPendientesAutorizar, 1)} h pendientes ({t.registrosPendientes}) · la
              autorización descontó {nf(t.ajusteAutorizacion, 1)} h de lo marcado
            </span>
          </div>
        </div>

        <div className="kpi">
          <span className="eyebrow">Proyección de cierre</span>
          <div className="kpi-val num">
            {nf(t.proyeccionCierre)}
            <span className="kpi-unit">h</span>
          </div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              de {nf(t.planeadasMes)} h planeadas en el mes
              {t.planeadasMes && !sinDiasCerrados
                ? ` (${nf((t.proyeccionCierre / t.planeadasMes) * 100, 1)}%)`
                : ''}
              {t.excesoProyectado > 0 ? ` · ${nf(t.excesoProyectado)} h de exceso al cierre` : ''}
            </span>
          </div>
        </div>

        <div className="kpi">
          <span className="eyebrow">Horas extras</span>
          <div className="kpi-val num" style={t.horasExtras > 0 ? { color: 'var(--warn-ink)' } : undefined}>
            {nf(t.horasExtras, 1)}
            <span className="kpi-unit">h</span>
          </div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              Exceso sobre {data.limiteSemanal} h/semana · {t.empleadosSobreLimite} empleados ya
              pasaron el límite en la semana del corte
            </span>
          </div>
        </div>

        <div className="kpi">
          <span className="eyebrow">Horas nocturnas</span>
          <div className="kpi-val num">
            {nf(t.horasNocturnas, 1)}
            <span className="kpi-unit">h</span>
          </div>
          <div className="kpi-foot">
            <span className="kpi-sub">
              7:00 p. m. a 6:00 a. m.
              {trabajadas > 0 ? ` · ${nf((t.horasNocturnas / trabajadas) * 100, 1)}% de lo trabajado` : ''}
            </span>
          </div>
        </div>
      </div>

      <CostoNominaMes costo={costo} />

      {conSobrecosto.length > 0 && (
        <div className="panel" style={{ marginTop: 12 }}>
          <div className="card-head" style={{ padding: '12px 16px 0' }}>
            <h3>Centros con sobrecosto de personal</h3>
            <span className="spacer" />
            <span className="eyebrow">{conSobrecosto.length} centros</span>
          </div>
          <div className="alert-list">
            {conSobrecosto.map((c) => (
              <FilaAlerta
                key={c.id}
                alerta={{
                  tipo: ['sobrecosto_critico', 'sin_datos'].includes(c.estado)
                    ? 'sobrecosto_critico'
                    : 'sobrecosto',
                  nombre: c.nombre,
                  meta: [
                    c.planeadasACorte
                      ? `${nf(c.ejecutadasACorte, 1)} h ejecutadas de ${nf(c.planeadasACorte, 1)} h planeadas`
                      : 'Sin planificación en el mes',
                    c.ejecutadasSinPlan ? `${nf(c.ejecutadasSinPlan, 1)} h en semanas sin planificar` : null,
                    c.horasExtras ? `${nf(c.horasExtras, 1)} h extras` : null,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                  etiqueta: `+${nf(c.horasNoPlaneadas, 1)} h no planeadas`,
                  detalle:
                    c.cumplimiento != null
                      ? `${nf(c.cumplimiento, 1)} % del plan${
                          c.excesoProyectado ? ` · cerraría +${nf(c.excesoProyectado)} h` : ''
                        }`
                      : 'todo lo trabajado está fuera de plan',
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="panel tbl-scroll" style={{ marginTop: 12 }}>
        <table className="data">
          <thead>
            <tr>
              <th>Centro</th>
              <th>Cumplimiento</th>
              <th>Planeadas</th>
              <th>Ejecutadas</th>
              <th>Diferencia</th>
              <th>Proyección</th>
              <th>Autorizado</th>
              <th>Sin plan</th>
              <th>Extras</th>
              <th>Nocturnas</th>
              <th>&gt; {data.limiteSemanal} h</th>
              <th title="Costo total empresa si se cumple la planificación del mes">Costo plan.</th>
              <th title="Costo total empresa: lo marcado a hoy + lo planeado de lo que falta (en un mes cerrado, el real)">
                Costo proy.
              </th>
              <th>Dif. costo</th>
              <th aria-label="Detalle" />
            </tr>
          </thead>
          <tbody>
            {data.centros.map((c) => {
              const e = ESTADO_HORAS[c.estado];
              return (
                  <tr key={c.id} onClick={() => setDetalle(c)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{c.nombre}</div>
                    </td>
                    <td>
                      <div className="bar-cell">
                        <span className="pill" style={{ '--sev': e.sev, '--sev-ink': e.ink }}>
                          <i className="dot" />
                          {c.cumplimiento == null ? e.texto : `${nf(c.cumplimiento, 1)} %`}
                        </span>
                        <div className="bar-track" style={{ width: 64 }}>
                          <div
                            className="bar-fill"
                            style={{ width: `${Math.min(100, c.cumplimiento || 0)}%`, background: e.sev }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>{nf(c.planeadasACorte, 1)}</td>
                    <td>{nf(c.ejecutadasACorte, 1)}</td>
                    <td style={c.diferencia < 0 ? { color: 'var(--critical-ink)' } : undefined}>
                      {c.diferencia > 0 ? '+' : ''}
                      {nf(c.diferencia, 1)}
                    </td>
                    <td>
                      {nf(c.proyeccionCierre)}
                      <span style={{ color: 'var(--ink-3)' }}> / {nf(c.planeadasMes)}</span>
                    </td>
                    <td
                      title={`${nf(c.horasAutorizadas, 1)} h autorizadas · ${nf(
                        c.horasPendientesAutorizar,
                        1,
                      )} h pendientes · la autorización descontó ${nf(c.ajusteAutorizacion, 1)} h`}
                    >
                      <div
                        style={{
                          color: c.porcentajeAutorizado < 80 ? 'var(--warn-ink)' : undefined,
                        }}
                      >
                        {nf(c.porcentajeAutorizado, 0)} %
                      </div>
                      {c.horasPendientesAutorizar > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                          {nf(c.horasPendientesAutorizar, 1)} h pend.
                        </div>
                      )}
                    </td>
                    <td style={c.ejecutadasSinPlan > 0 ? { color: 'var(--warn-ink)' } : undefined}>
                      {c.ejecutadasSinPlan ? nf(c.ejecutadasSinPlan, 1) : '—'}
                    </td>
                    <td style={c.horasExtras > 0 ? { color: 'var(--warn-ink)' } : undefined}>
                      {c.horasExtras ? nf(c.horasExtras, 1) : '—'}
                    </td>
                    <td>{c.horasNocturnas ? nf(c.horasNocturnas, 1) : '—'}</td>
                    <td>{c.empleadosSobreLimite || '—'}</td>
                    <CeldasCosto c={costoPorCentro.get(c.id)} cargando={costo?.loading} />
                    <td>
                      <button
                        type="button"
                        className="ctl"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setDetalle(c);
                        }}
                      >
                        Ver por días
                      </button>
                    </td>
                  </tr>
              );
            })}
            {!data.centros.length && (
              <tr>
                <td colSpan={15} style={{ textAlign: 'center', color: 'var(--ink-3)' }}>
                  No hay horas planeadas ni ejecutadas en el mes.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="card-sub" style={{ marginTop: 8 }}>
        {data.horaCorte &&
          `Hoy cuenta en vivo: lo planeado hasta las ${data.horaCorte} contra lo trabajado hasta ahora (los turnos abiertos cuentan hasta este momento, con la hora de entrada marcada, así que una llegada anticipada suma). `}
        Solo se comparan las semanas que el centro planificó; lo trabajado en semanas sin
        planificación aparece en «Sin plan». Ejecutadas = horas autorizadas o, si aún no se autorizan,
        las marcadas: al autorizar un registro (ajuste de entrada/salida y almuerzo) el cumplimiento
        baja en lo que se descontó. «Autorizado» = parte de las horas ejecutadas que ya está
        autorizada; lo pendiente todavía puede bajar al autorizarse.
        {data.centrosExcluidos > 0 && ` ${data.centrosExcluidos} centros están excluidos del análisis.`} Extras = lo que excede {data.limiteSemanal} h por empleado en la semana (lunes a
        domingo). Semáforo: 95–100 % en meta, 85–95 % alerta, &lt;85 % crítico; por encima del 100 % es
        sobrecosto de personal (en rojo si supera el 110 %). Los conductores no se cuentan en los
        centros: se muestran aparte.
      </p>

      <ConductoresDelMes conductores={data.conductores} limiteSemanal={data.limiteSemanal} />

      {detalle && (
        <DetalleCentroModal
          centro={detalle}
          mesEnCurso={data.mesEnCurso}
          horaCorte={data.horaCorte}
          mes={data.mes}
          onCerrar={() => setDetalle(null)}
        />
      )}
    </>
  );
};

const relojBogota = () => {
  const ahora = new Date();
  const fecha = ahora.toLocaleDateString('es-CO', {
    timeZone: 'America/Bogota',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const hora = ahora.toLocaleTimeString('es-CO', {
    timeZone: 'America/Bogota',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${fecha}, ${hora}`;
};

const DashboardPage = () => {
  const personal = useSeccion(useCallback(() => getPersonalTrabajando(), []));
  const produccion = useSeccion(useCallback(() => getProduccion(), []));
  const tendencia = useSeccion(useCallback(() => getTendencia(7), []));
  const calidad = useSeccion(useCallback(() => getCalidad(7), []));
  const conductores = useSeccion(useCallback(() => getConductores(), []));
  const servicios = useSeccion(useCallback(() => getServiciosPublicos(), []));
  const turnos = useSeccion(useCallback(() => getPlanificacionTurnos(), []));
  const [mesHoras, setMesHoras] = useState(mesActual());
  const [verFotos, setVerFotos] = useState(false);
  const [planCentro, setPlanCentro] = useState(null);
  const horas = useSeccion(useCallback(() => getCumplimientoHoras(mesHoras), [mesHoras]));
  // El costo de la nómina se reparte en los mismos centros que muestra «Horas del mes».
  const centrosHoras = horas.data?.mes === mesHoras ? horas.data.centros.map((c) => c.id).join(',') || 'ninguno' : null;
  const costoNomina = useSeccion(
    useCallback(
      () => (centrosHoras ? getCostoNominaMes(mesHoras, centrosHoras) : Promise.resolve(null)),
      [mesHoras, centrosHoras],
    ),
  );
  // Centros fuera de cada sección (para los botones) y sección cuyo filtro está abierto.
  const resumenSecciones = useSeccion(useCallback(() => getResumenSecciones(), []));
  const agentes = useSeccion(useCallback(() => getAgentes(), []));
  const [filtroSeccion, setFiltroSeccion] = useState(null);

  const secciones = [personal, produccion, tendencia, calidad, conductores, servicios, turnos, horas];

  // Qué datos recargar al cambiar los centros de cada sección del panel.
  const recargaPorSeccion = {
    operacion: [personal, produccion, tendencia],
    personal: [personal],
    calidad: [calidad],
    centros: [produccion],
    horas: [horas],
    servicios: [servicios],
    turnos: [turnos],
    conductores: [conductores],
    // Los agentes usan el filtro en su próximo análisis; no hay nada que recargar.
    agentes: [],
  };
  const botonCentros = (seccion, compacto) => (
    <BotonCentros
      seccion={seccion}
      resumen={resumenSecciones.data}
      compacto={compacto}
      onAbrir={() => setFiltroSeccion(seccion)}
    />
  );
  const cargando = secciones.some((s) => s.loading);

  const [tab, setTab] = useState('todas');
  const [verTodas, setVerTodas] = useState(false);
  const [verCalidad, setVerCalidad] = useState(false);
  const [ultimaCarga, setUltimaCarga] = useState(Date.now());
  const [reloj, setReloj] = useState(relojBogota());
  const [segundos, setSegundos] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setReloj(relojBogota());
      setSegundos(Math.floor((Date.now() - ultimaCarga) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [ultimaCarga]);

  const recargarTodo = () => {
    secciones.forEach((s) => s.recargar());
    setUltimaCarga(Date.now());
    setSegundos(0);
  };

  const turnosTotales = personal.data
    ? personal.data.totales.trabajando +
      personal.data.totales.finalizados +
      personal.data.totales.noPresentados
    : 0;
  const turnosCubiertos = personal.data
    ? personal.data.totales.trabajando + personal.data.totales.finalizados
    : 0;
  const cobertura = turnosTotales ? (turnosCubiertos / turnosTotales) * 100 : 0;

  const alertas = useMemo(() => {
    const lista = [];
    (personal.data?.atencion || []).forEach((e) => {
      if (e.tipo === 'repetida') {
        lista.push({
          clave: `${e.tipo}-${e.id}-${e.fecha}`,
          tipo: e.tipo,
          nombre: e.nombre,
          meta: `${e.lavanderia}${e.rol ? ` · ${e.rol}` : ''}`,
          etiqueta: `${e.marcaciones} marcaciones`,
          detalle: fechaCortaAlerta(e.fecha),
          nota: e.detalle,
          orden: 0,
        });
        return;
      }
      lista.push({
        clave: `${e.tipo}-${e.id}`,
        tipo: e.tipo,
        nombre: e.nombre,
        meta: `${e.lavanderia}${e.rol ? ` · ${e.rol}` : ''}`,
        etiqueta:
          e.tipo === 'tarde' ? `${e.minutosRetraso} min tarde` : 'Sin marcar',
        detalle:
          e.tipo === 'tarde'
            ? `turno ${e.horaPlanificada} · entró ${e.horaIngreso}`
            : `turno ${e.horaPlanificada || '—'} · sin presentarse`,
        orden: e.minutosRetraso || 0,
      });
    });
    const prioridad = { no_presentado: 0, tarde: 1, repetida: 2 };
    return lista.sort(
      (a, b) => prioridad[a.tipo] - prioridad[b.tipo] || b.orden - a.orden,
    );
  }, [personal.data]);

  const conteos = {
    todas: alertas.length,
    no_presentado: alertas.filter((a) => a.tipo === 'no_presentado').length,
    tarde: alertas.filter((a) => a.tipo === 'tarde').length,
    repetida: alertas.filter((a) => a.tipo === 'repetida').length,
  };

  const filtradas = tab === 'todas' ? alertas : alertas.filter((a) => a.tipo === tab);
  const TOPE = 8;
  const visibles = verTodas ? filtradas : filtradas.slice(0, TOPE);

  const calidadConRechazo = (calidad.data?.centros || []).filter(
    (c) => c.kilosRechazo > 0,
  );
  const calidadVisible = verCalidad
    ? calidadConRechazo
    : calidadConRechazo.slice(0, TOPE);

  return (
    <div className="sala-control">
      <header className="topbar">
        <div className="topbar-in">
          <div className="brand">
            <h1>
              <span className="pulse" aria-hidden="true" />
              Sala de Control de Lavado
            </h1>
            <div className="sub">
              {reloj}
              {servicios.data ? ` · ${servicios.data.totalCentros} centros` : ''}
              {` · actualizado hace ${segundos} s`}
            </div>
          </div>
          <div className="controls">
            <button className="ctl" type="button" onClick={recargarTodo} disabled={cargando}>
              <svg
                className={cargando ? 'spin' : ''}
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M21 12a9 9 0 1 1-3-6.7" />
                <path d="M21 3v6h-6" />
              </svg>
              Actualizar
            </button>
            <button className="ctl" type="button" onClick={() => setVerFotos(true)}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4 7h3l2-3h6l2 3h3v13H4z" />
                <circle cx="12" cy="13" r="3.5" />
              </svg>
              Revisar uniformes
            </button>
            <Link className="ctl" to="/control-empleados">
              Control de personal
            </Link>
          </div>
        </div>
      </header>

      <div className="wrap">
        <section aria-labelledby="kpi-h">
          <div className="sec-head">
            <h2 id="kpi-h">Estado de la operación</h2>
            <span className="note">
              {resumenSecciones.data?.operacion?.excluidos
                ? `${resumenSecciones.data.operacion.excluidos} centros fuera · hoy`
                : 'Todos los centros · hoy'}
            </span>
            <span className="spacer" />
            {botonCentros('operacion')}
          </div>

          {personal.error || produccion.error ? (
            <div className="panel empty">{personal.error || produccion.error}</div>
          ) : !personal.data || !produccion.data ? (
            <div className="kpis">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="kpi" style={{ minHeight: 132 }}>
                  <span className="eyebrow">Cargando…</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="kpis">
              <div className="kpi lead">
                <div className="kpi-top">
                  <div>
                    <span className="eyebrow">Cobertura de turnos</span>
                    <div className="kpi-val num">
                      {nf(cobertura, 1)}
                      <span className="kpi-unit">%</span>
                    </div>
                  </div>
                </div>
                <div className="meter">
                  {[
                    ['var(--good)', personal.data?.totales.trabajando],
                    ['var(--s1)', personal.data?.totales.finalizados],
                    ['var(--critical)', personal.data?.totales.noPresentados],
                  ]
                    .filter(([, v]) => v > 0)
                    .map(([c, v]) => (
                      <i key={c} style={{ background: c, flex: v }} />
                    ))}
                </div>
                <div className="legend-inline">
                  <span>
                    <i className="dot" style={{ background: 'var(--good)' }} />
                    trabajando {personal.data?.totales.trabajando ?? '—'}
                  </span>
                  <span>
                    <i className="dot" style={{ background: 'var(--s1)' }} />
                    jornada cerrada {personal.data?.totales.finalizados ?? '—'}
                  </span>
                  <span>
                    <i className="dot" style={{ background: 'var(--critical)' }} />
                    sin presentarse {personal.data?.totales.noPresentados ?? '—'}
                  </span>
                </div>
                <div className="kpi-foot">
                  <span className="kpi-sub">
                    {turnosCubiertos} de {turnosTotales} turnos ·{' '}
                    {personal.data?.totales.tarde ?? 0} con retraso
                  </span>
                </div>
              </div>

              <Kpi
                label="Kilos recibidos"
                valor={nf(produccion.data?.recepcion.kilos, 2)}
                unidad="kg"
                sub={`${produccion.data?.recepcion.lotes ?? 0} lotes · ${nf(
                  produccion.data?.recepcion.kilosLimpios,
                  2,
                )} kg limpios`}
                serie={tendencia.data?.kilos}
                variacion={tendencia.data?.variacion.kilos}
                color="var(--s1)"
              />

              <Kpi
                label="Prendas alistadas"
                valor={nf(produccion.data?.alistamientos.prendas)}
                sub={`${produccion.data?.alistamientos.registros ?? 0} alistamientos registrados`}
                serie={tendencia.data?.prendas}
                variacion={tendencia.data?.variacion.prendas}
                color="var(--s3)"
              />

              <Kpi
                label="Rechazo sobre recibido"
                valor={nf(produccion.data?.recepcion.porcentajeRechazo, 2)}
                unidad="%"
                sub={`${nf(produccion.data?.recepcion.kilosRechazo, 2)} kg rechazados`}
                serie={tendencia.data?.rechazo}
                variacion={tendencia.data?.variacion.rechazo}
                color="var(--s2)"
                invertido
              />
            </div>
          )}
        </section>

        <section aria-labelledby="alert-h">
          <div className="sec-head">
            <h2 id="alert-h">Personal que requiere atención</h2>
            <span className="note">
              {conteos.no_presentado} sin presentarse · {conteos.tarde} con retraso ·{' '}
              {conteos.repetida} marcaciones repetidas (7 días)
            </span>
            <span className="spacer" />
            {botonCentros('personal')}
          </div>
          <div className="panel">
            <div className="alert-tabs" role="group" aria-label="Filtrar alertas">
              {[
                ['todas', 'Todas'],
                ['no_presentado', 'Sin presentarse'],
                ['tarde', 'Retrasos'],
                ['repetida', 'Repetidas'],
              ].map(([clave, texto]) => (
                <button
                  key={clave}
                  type="button"
                  className="tab"
                  aria-pressed={tab === clave}
                  onClick={() => { setTab(clave); setVerTodas(false); }}
                >
                  {texto}
                  <b>{conteos[clave]}</b>
                </button>
              ))}
            </div>

            {visibles.length === 0 ? (
              <div className="empty">
                Sin novedades en este filtro. Toda la operación va según lo planificado.
              </div>
            ) : (
              <div className="alert-list">
                {visibles.map((a) => (
                  <FilaAlerta key={a.clave} alerta={a} />
                ))}
              </div>
            )}

            {filtradas.length > TOPE && (
              <button className="alert-more" type="button" onClick={() => setVerTodas(!verTodas)}>
                {verTodas
                  ? 'Mostrar solo las 8 más urgentes'
                  : `Ver las ${filtradas.length} novedades`}
              </button>
            )}
          </div>
        </section>

        <section aria-labelledby="cal-h">
          <div className="sec-head">
            <h2 id="cal-h">Mayor rechazo de la semana</h2>
            <span className="note">
              {calidad.data
                ? `${calidad.data.desde} a ${calidad.data.hasta} · ${nf(
                    calidad.data.totales.kilosRechazo,
                    2,
                  )} kg rechazados de ${nf(calidad.data.totales.kilos, 1)} kg recibidos (${nf(
                    calidad.data.totales.porcentajeRechazo,
                    2,
                  )}%) · ${calidad.data.totales.centrosSobreLimite} sobre su límite`
                : 'Últimos 7 días'}
            </span>
            <span className="spacer" />
            {botonCentros('calidad')}
          </div>
          <div className="panel">
            {calidad.error ? (
              <div className="empty">{calidad.error}</div>
            ) : !calidadVisible.length ? (
              <div className="empty">
                {calidad.loading
                  ? 'Cargando…'
                  : 'No hay kilos rechazados registrados en la semana.'}
              </div>
            ) : (
              <div className="alert-list">
                {calidadVisible.map((c) => (
                  <FilaAlerta
                    key={c.id}
                    alerta={{
                      tipo: c.excedeLimite ? 'rechazo' : 'neutro',
                      nombre: c.nombre,
                      meta: `${nf(c.kilosRechazo, 2)} kg rechazados de ${nf(
                        c.kilos,
                        1,
                      )} kg · ${c.lotes} lotes`,
                      etiqueta: `${nf(c.porcentajeRechazo, 2)}% de rechazo`,
                      detalle: c.limiteRechazo
                        ? `límite del centro ${nf(c.limiteRechazo, 2)}%`
                        : 'sin límite parametrizado',
                    }}
                  />
                ))}
              </div>
            )}
            {calidadConRechazo.length > TOPE && (
              <button
                className="alert-more"
                type="button"
                onClick={() => setVerCalidad(!verCalidad)}
              >
                {verCalidad
                  ? 'Mostrar solo los 8 con mayor rechazo'
                  : `Ver los ${calidadConRechazo.length} centros con rechazo`}
              </button>
            )}
          </div>
        </section>

        <section aria-labelledby="cen-h">
          <div className="sec-head">
            <h2 id="cen-h">Centros de lavado</h2>
            <span className="spacer" />
            <span className="note">
              {produccion.data
                ? `${produccion.data.porCentro.length} centros con movimiento · ${nf(
                    produccion.data.recepcion.kilos,
                    1,
                  )} kg · ${produccion.data.recepcion.lotes} lotes`
                : ''}
            </span>
            {botonCentros('centros')}
          </div>
          <div className="panel tbl-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Centro</th>
                  <th>Kilos recibidos</th>
                  <th>Prendas alistadas</th>
                  <th>Lotes</th>
                  <th>Rechazo</th>
                </tr>
              </thead>
              <tbody>
                {(produccion.data?.porCentro || []).map((c) => {
                  const maxKg = Math.max(
                    ...(produccion.data?.porCentro || []).map((x) => x.kilosRecibidos || 0),
                    1,
                  );
                  const excede = c.limiteRechazo > 0 && c.porcentajeRechazo > c.limiteRechazo;
                  return (
                    <tr key={c.id}>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{c.nombre}</div>
                        {c.ciudad && <div className="eyebrow">{c.ciudad}</div>}
                      </td>
                      <td>
                        <div className="bar-cell">
                          <span>{nf(c.kilosRecibidos, 1)}</span>
                          <div className="bar-track" style={{ width: 64 }}>
                            <div
                              className="bar-fill"
                              style={{
                                width: `${Math.round((c.kilosRecibidos / maxKg) * 100)}%`,
                                background: 'var(--s1)',
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      <td>{c.prendasAlistadas ? nf(c.prendasAlistadas) : '—'}</td>
                      <td>{c.lotes}</td>
                      <td style={excede ? { color: 'var(--warn-ink)' } : undefined}>
                        {c.kilosRechazo ? `${nf(c.porcentajeRechazo, 2)} %` : '—'}
                      </td>
                    </tr>
                  );
                })}
                {!produccion.data?.porCentro?.length && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)' }}>
                      Sin movimiento registrado hoy.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section aria-labelledby="horas-h">
          <div className="sec-head">
            <h2 id="horas-h">Horas del mes: planeadas vs. ejecutadas</h2>
            <span className="note">
              {horas.data
                ? `${
                    horas.data.mesEnCurso
                      ? `Mes en curso · en vivo hasta las ${horas.data.horaCorte}`
                      : `${nombreMes(horas.data.mes)} (cerrado) · corte al ${fechaCorta(horas.data.corte)}`
                  } · ${horas.data.totales.centrosConSobrecosto} con sobrecosto · ${
                    horas.data.totales.centrosCriticos
                  } por debajo del 85 % · ${horas.data.totales.centrosEnAlerta} en alerta`
                : 'Cargando…'}
            </span>
            <span className="spacer" />
            <input
              type="month"
              className="ctl"
              aria-label="Mes del análisis"
              value={mesHoras}
              max={mesActual()}
              onChange={(e) => e.target.value && setMesHoras(e.target.value)}
            />
            {botonCentros('horas')}
          </div>
          <HorasDelMes seccion={horas} costo={costoNomina} />
        </section>

        <section aria-labelledby="cump-h">
          <div className="sec-head">
            <h2 id="cump-h">Cumplimiento por centro</h2>
            <span className="note">
              Servicios públicos de hoy · planificación de la semana en curso
            </span>
          </div>
          <div className="card-grid">
            <div className="card">
              <div className="card-head">
                <h3>Servicios públicos</h3>
                <span className="spacer" />
                <span className="eyebrow">Hoy</span>
                {botonCentros('servicios', true)}
              </div>
              <p className="card-sub">
                Medidores con lectura registrada frente a los configurados en cada centro.
              </p>
              {servicios.data && (
                <>
                  <Medidor
                    hechos={servicios.data.medidoresLlenados}
                    total={servicios.data.totalMedidores}
                    porcentaje={servicios.data.porcentaje}
                    unidad="medidores"
                  />
                  <div className="kpi-sub" style={{ marginTop: 8 }}>
                    {servicios.data.centrosCompletos} de {servicios.data.totalCentros} centros
                    completos
                    {servicios.data.centrosSinMedidores > 0
                      ? ` · ${servicios.data.centrosSinMedidores} sin medidores configurados`
                      : ''}
                  </div>
                  <div className="check-list">
                    {servicios.data.centros.map((c) => {
                      const estado = c.sinMedidores
                        ? 'neutro'
                        : c.completo
                          ? 'listo'
                          : c.llenados > 0
                            ? 'parcial'
                            : 'pendiente';
                      return (
                        <div className="check-row" key={c.id}>
                          <span className="n" title={c.nombre}>{c.nombre}</span>
                          <span className={`v ${estado}`}>
                            {c.sinMedidores ? (
                              'sin medidores'
                            ) : (
                              <>
                                {c.llenados}/{c.medidores}
                                {estado === 'listo' ? ' ✓' : ''}
                              </>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="card">
              <div className="card-head">
                <h3>Planificación de turnos</h3>
                <span className="spacer" />
                <span className="eyebrow">
                  {turnos.data ? `${turnos.data.weekStart} a ${turnos.data.weekEnd}` : ''}
                </span>
                {botonCentros('turnos', true)}
              </div>
              <p className="card-sub">Centros con turnos planificados esta semana.</p>
              {turnos.data && (
                <>
                  <Medidor
                    hechos={turnos.data.centrosConPlanificacion}
                    total={turnos.data.totalCentros}
                    porcentaje={turnos.data.porcentaje}
                    unidad="centros"
                  />
                  <div className="kpi-sub" style={{ marginTop: 8 }}>
                    {turnos.data.totalCentros - turnos.data.centrosConPlanificacion} sin
                    planificación · {nf(turnos.data.empleadosAsignados)} empleados asignados
                  </div>
                  <div className="check-list">
                    {turnos.data.centros.map((c) => (
                      <div className="check-row" key={c.id}>
                        <span className="n" title={c.nombre}>{c.nombre}</span>
                        <span className={`v ${c.planificado ? 'listo' : 'pendiente'}`}>
                          {c.planificado
                            ? `${c.empleadosAsignados} empleados ✓`
                            : 'sin planificar'}
                          <button
                            type="button"
                            className="ctl"
                            style={{ padding: '2px 8px', fontSize: 11 }}
                            onClick={() => setPlanCentro(c)}
                          >
                            Ver semana
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <section aria-labelledby="drv-h">
          <div className="sec-head">
            <h2 id="drv-h">Rutas y conductores</h2>
            <span className="note">
              {conductores.data
                ? `${conductores.data.totales.entregas} entregas · ${conductores.data.totales.recolecciones} recolecciones · ${conductores.data.totales.conductores} conductores · ${nf(
                    conductores.data.totales.prendas,
                  )} prendas movidas`
                : ''}
            </span>
            <span className="spacer" />
            {botonCentros('conductores')}
          </div>
          <div className="panel tbl-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Conductor</th>
                  <th>Entregas</th>
                  <th>Recolecciones</th>
                  <th>Prendas movidas</th>
                </tr>
              </thead>
              <tbody>
                {(conductores.data?.conductores || []).map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.nombre || 'Sin nombre'}</td>
                    <td>{c.entregas}</td>
                    <td>{c.recolecciones}</td>
                    <td>{nf(c.prendas)}</td>
                  </tr>
                ))}
                {!conductores.data?.conductores?.length && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', color: 'var(--ink-3)' }}>
                      Ningún conductor ha registrado movimientos hoy.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <p className="foot">Datos en vivo de todos los centros · zona horaria de Colombia</p>
        {verFotos && <FotosUniformeModal onCerrar={() => setVerFotos(false)} />}
        <AgentesBurbuja seccion={agentes} botonCentros={botonCentros} />
        {filtroSeccion && (
          <FiltroCentrosModal
            seccion={filtroSeccion}
            onCerrar={() => setFiltroSeccion(null)}
            onGuardado={(seccion) => {
              setFiltroSeccion(null);
              resumenSecciones.recargar();
              (recargaPorSeccion[seccion] || []).forEach((s) => s.recargar());
            }}
          />
        )}
        {planCentro && (
          <PlanificacionCentroModal
            centro={planCentro}
            semanaInicial={turnos.data?.weekStart}
            onCerrar={() => setPlanCentro(null)}
          />
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
